/* app/navConfig.js — единый источник навигации (desktop / mobile).
   Один состав разделов (PRIMARY_NAV); меняется контейнер и видимый срез:
     sidebar (desktop) — все 4 раздела по порядку;
     tab-bar (mobile)  — те же 4 раздела + «Ещё»;
     «Ещё»-шторка      — Уведомления + Аккаунт.
   Раздел «Заведение» убран из навигации: профиль и база знаний живут в
   Настройках → «Для бизнеса» (доступ только у бизнес-пространств);
   легаси /restaurant редиректит туда (routes.jsx).
   Источник: 02-IA §3-4 (§3.5 — мобильная IA). Порядок и пути зафиксированы. */

  import { isSectionEnabled } from '../lib/planSections.js';
  // №524: раздел «Отчёты» выключен к релизу 11.11 — рубильник в lib/reportsSection.js
  import { REPORTS_SECTION_ENABLED } from '../lib/reportsSection.js';
  import { canUseAssistant } from '../lib/assistantAccess.js';

  // Основные разделы рабочего пространства
  const PRIMARY_NAV = [
    // brand — имя ИИ-ассистента «Аргус» (№525): подпись выделена цветом --argus
    // assistant — пункт виден, только если участнику открыт Аргус (№523, workspace.assistant)
    { id: 'chat',       label: 'Аргус',      to: '/chat',       icon: 'chat', brand: true, assistant: true },
    { id: 'kitchen',    label: 'Продукция',  to: '/kitchen',    icon: 'kitchen' },
    // section — тарифный рубильник ПРОСТРАНСТВА (workspace.sections.analytics === false
    // прячет пункт у всех, включая владельца); см. isNavItemVisible и lib/planSections.js
    { id: 'analytics',  label: 'Аналитика',  to: '/analytics',  icon: 'analytics', section: 'analytics' },
    ...(REPORTS_SECTION_ENABLED
      ? [{ id: 'reports',    label: 'Отчёты',     to: '/reports',    icon: 'reports' }]
      : []),
    // Обучение — раздел ученика «Академия»: доступен любому авторизованному,
    // но только пока включён рубильник ACADEMY_ENABLED (флаг academy_enabled
    // из /api/auth/me) — см. isNavItemVisible ниже
    { id: 'learning',   label: 'Обучение',   to: '/learning',   icon: 'book', flag: 'academy_enabled' },
    // Админка — хаб внутренних сервисов (CRM, рассылки и др.); consoleOnly
    // фильтруется по user.console_access (есть доступ к любому модулю консоли)
    { id: 'admin',      label: 'Админка',    to: '/console',    icon: 'shield', consoleOnly: true },
  ];

  // Аккаунт — отдельно (низ сайдбара / «Ещё» на mobile)
  const ACCOUNT_NAV = { id: 'settings', label: 'Аккаунт', to: '/settings', icon: 'settings' };

  // Пункт по id, а не по индексу: состав PRIMARY_NAV зависит от рубильников.
  const navItem = (id) => PRIMARY_NAV.find((item) => item.id === id);

  // Нижний таб-бар (mobile): основные разделы + «Ещё»
  const MOBILE_TABS = [
    navItem('chat'),
    navItem('kitchen'),
    navItem('analytics'),
    ...(REPORTS_SECTION_ENABLED ? [navItem('reports')] : []),
    { id: 'more', label: 'Ещё', to: '#more', icon: 'more-horizontal' }, // открывает шторку
  ];

  // «Ещё»-шторка: уведомления + поддержка + аккаунт.
  // «Поддержка» на xs живёт ТОЛЬКО здесь: в шапке иконку не показываем — тесно;
  // на sm+ вход — иконка headset в правом кластере шапки (SupportButton). Переключатель пространства, командная
  // палитра и «Выйти» рендерит MoreSheet отдельными блоками (не пункты навигации)
  // — см. MobileTabBar.jsx. 02-IA §3.5.
  const MORE_SHEET = [
    { id: 'notifications', label: 'Уведомления', to: '/notifications', icon: 'notifications' },
    { id: 'support', label: 'Поддержка', to: '/support', icon: 'headset' },
    navItem('learning'),  // Обучение (раздел ученика, доступен всем)
    navItem('admin'),     // Админка (consoleOnly — фильтруется по user.console_access в MobileTabBar)
    ACCOUNT_NAV,    // Аккаунт
    // «Подарить Ресториум». На десктопе живёт в меню профиля внизу сайдбара, но
    // на телефоне сайдбара нет вовсе — и без этой строки покупка подарка была бы
    // недоступна с телефона целиком. Проверено живым прогоном на 380 px:
    // `.user-menu` там не отрисован.
    { id: 'gift', label: 'Подарить Ресториум', to: '/gift', icon: 'gift' },
  ];

  // Видимость пункта — ЕДИНОЕ правило для сайдбара, таб-бара и шторки «Ещё»
  // (раньше каждый контейнер знал только про consoleOnly):
  //   consoleOnly — пункт хаба «Админка» (нужен доступ хоть к одному модулю);
  //   flag        — глобальный рубильник раздела из /api/auth/me (academy_enabled и т.п.);
  //   section     — рубильник ПРОСТРАНСТВА из тарифа: workspace.sections[section] === false
  //                 прячет пункт (Family Garden: аналитику не видит никто, и владелец тоже).
  //                 workspace — необязательный третий аргумент; без него, без sections
  //                 или без пространства пункт виден, как до флагов.
  // Выключенный раздел обязан исчезать из меню целиком: пункт, ведущий на
  // экран «раздел отключён», — это обещание функции, которой нет.
  function isNavItemVisible(item, user, workspace) {
    if (!item) return false;
    if (item.consoleOnly && !(user && user.console_access)) return false;
    if (item.flag && !(user && user[item.flag])) return false;
    if (item.section && !isSectionEnabled(workspace, item.section)) return false;
    if (item.assistant && !canUseAssistant(workspace)) return false;
    return true;
  }

  // Срез списка пунктов по тому же правилу (сайдбар, шторка «Ещё»).
  function visibleNavItems(items, user, workspace) {
    return (items || []).filter((item) => isNavItemVisible(item, user, workspace));
  }

  // Вкладки нижнего таб-бара: MOBILE_TABS минус закрытые разделы. «Ещё» правил не имеет и
  // остаётся последней; таб-бар — grid с auto-columns 1fr, поэтому 4 вкладки вместо 5
  // ложатся ровно (shell.css `.tab-bar`).
  function mobileTabsFor(user, workspace) {
    return visibleNavItems(MOBILE_TABS, user, workspace);
  }

  window.navConfig = { PRIMARY_NAV, ACCOUNT_NAV, MOBILE_TABS, MORE_SHEET, isNavItemVisible, visibleNavItems, mobileTabsFor };

const __ex_navConfig = window.navConfig; export { __ex_navConfig as navConfig };
